<template>
  <div class="relative flex flex-col min-w-0 break-words w-full">
    <div class="rounded-t mb-0 pb-3 border-0">
      <div class="flex flex-wrap items-center justify-between">
        <div class="relative flex-grow flex-1">
          <h3 class="text-3xl text-blueGray-700 font-headline mt-4 mx-5">
            Organizations
          </h3>
        </div>
        <div class="mt-4 mr-5">
          <router-link
            to="/admin/client-cards"
            class="inline-flex items-center px-4 py-2 bg-tiara-600 hover:bg-tiara-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors duration-150"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
            Client Cards Dashboard
          </router-link>
        </div>
      </div>
    </div>
    <DataTable
      ref="dataTable"
      class=""
      :admin-prefix="adminPrefix"
      :api-prefix="apiPrefix"
      :singular-label="singularLabel"
      :plural-label="pluralLabel"
      :test-boolean-key="testBooleanKey"
      :columns="columns"
      :create-form="createForm"
      :update-form="updateForm"
      :actions="actions"
      :nested-level="0"
    />
    <regular-modal ref="cloneOrgModal">
      <template #header>
        <h4 class="text-xl font-headline">Clone Organization</h4>
      </template>
      <template #body>
        <div class="p-6 space-y-4">
          <p class="text-sm text-blueGray-600">
            Clone
            <strong>{{ cloneSourceOrg ? cloneSourceOrg.name : "" }}</strong>
            into a new test organization for demo and testing purposes.
          </p>
          <div>
            <label
              class="block text-xs font-bold uppercase text-blueGray-600 mb-1"
            >
              New Organization Name
            </label>
            <input
              v-model="cloneForm.name"
              type="text"
              class="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              placeholder="e.g. Acme Corp (Demo)"
            />
          </div>
          <div>
            <label
              class="block text-xs font-bold uppercase text-blueGray-600 mb-1"
            >
              Target Email Domain
            </label>
            <input
              v-model="cloneForm.targetDomain"
              type="text"
              class="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              placeholder="e.g. acme-demo.com"
            />
          </div>
          <div>
            <label
              class="block text-xs font-bold uppercase text-blueGray-600 mb-1"
            >
              Organization Logo
            </label>
            <div v-if="previewLogoUrl" class="flex items-center space-x-3 mb-2">
              <div
                class="bg-cover bg-center rounded-full border border-gray-200 shrink-0"
                :style="`background-image: url(${previewLogoUrl}); width: 56px; height: 56px;`"
              ></div>
              <div class="text-xs text-blueGray-500">
                <span
                  v-if="cloneForm.logoHash"
                  class="font-semibold text-green-600"
                  >New logo uploaded</span
                >
                <span v-else>Using source organization logo by default</span>
                <button
                  v-if="cloneForm.logoHash"
                  type="button"
                  class="block text-red-500 hover:underline mt-1 font-semibold"
                  @click="removeCloneLogo"
                >
                  Remove custom logo
                </button>
              </div>
            </div>
            <app-dropzone
              v-if="!cloneForm.logoHash"
              :max-files="1"
              :accept="['image/png', 'image/jpg', 'image/jpeg']"
              prompt="Upload logo for org & spaces..."
              @uploaded="setCloneLogo"
            ></app-dropzone>
          </div>
          <div>
            <label
              class="block text-xs font-bold uppercase text-blueGray-600 mb-1"
            >
              Initial Password for Anonymized Users
            </label>
            <input
              v-model="cloneForm.defaultPassword"
              type="text"
              class="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              placeholder="e.g. DemoAccess2026!"
            />
          </div>
          <div class="flex items-center space-x-2 pt-2">
            <input
              id="obfuscateProjects"
              v-model="cloneForm.obfuscateProjects"
              type="checkbox"
              class="rounded text-blueDianne focus:ring-blueDianne h-4 w-4"
            />
            <label for="obfuscateProjects" class="text-sm text-blueGray-700">
              Obfuscate project and collaboration request titles & descriptions
            </label>
          </div>
        </div>
      </template>
      <template #footer>
        <button
          class="modal-nonprimary-button mr-2"
          type="button"
          :disabled="isCloning"
          @click="closeCloneModal"
        >
          Cancel
        </button>
        <button
          class="modal-primary-button"
          type="button"
          :disabled="isCloning || !cloneForm.name || !cloneForm.targetDomain"
          @click="submitCloneOrg"
        >
          <span v-if="isCloning">Cloning...</span>
          <span v-else>Clone Organization</span>
        </button>
      </template>
    </regular-modal>
  </div>
</template>
<script>
import { utils } from "common-frontend";
import DataTable from "./DataTable.vue";
import RegularModal from "../Modals/Regular.vue";
import Dropzone from "../Dropzone.vue";

const { avatarUrl, logoUrl } = utils;

export default {
  name: "Organizations",
  components: {
    DataTable,
    RegularModal,
    AppDropzone: Dropzone,
  },
  data() {
    return {
      cloneSourceOrg: null,
      isCloning: false,
      cloneForm: {
        name: "",
        targetDomain: "demo.notedsource.com",
        obfuscateProjects: true,
        defaultPassword: "DemoAccess2026!",
        logoHash: null,
      },
      previewLogoUrl: "",
      singularLabel: "organization",
      pluralLabel: "organizations",
      adminPrefix: "organizations",
      apiPrefix: "organization",
      testBooleanKey: "isTestAccount",
      columns: {
        isTestAccount: {
          type: "testBoolean",
          label: "Is Test Account",
          hidden: "never",
          sort: {
            allow: false,
          },
          filter: {
            allow: true,
            matchType: "testBoolean",
            default: { label: "No", value: false },
          },
        },
        hashedId: {
          type: "formattedString",
          label: "Organization ID",
          hidden: "yes",
          format(row) {
            const els = [];
            els.push(
              `<span class="text-xs uppercase font-mono text-nowrap">org-${row.hashedId}</span>`,
            );
            return `
              <span class="text-nowrap">
                ${els.join("")}
              </span>
            `;
          },
          sort: {
            allow: false,
          },
          filter: {
            allow: true,
            matchType: "hashedId",
            prefix: "org-",
          },
        },
        createdAt: {
          type: "absoluteDate",
          label: "Created At",
          sort: {
            allow: true,
            default: "desc",
          },
          filter: {
            allow: false,
            matchType: "dateRange",
            value: "",
            // config: https://chmln.github.io/flatpickr/options/
            config: {
              mode: "range",
              maxDate: "today",
            },
          },
        },
        avatar: {
          type: "avatarWithActivityLevel",
          label: "Avatar",
          format(row) {
            return logoUrl(row);
          },
          sort: {
            allow: false,
          },
          filter: {
            allow: false,
          },
        },
        name: {
          type: "string",
          label: "Name",
          sort: {
            allow: true,
            default: null,
          },
          filter: {
            allow: true,
            matchType: "wildcard",
            value: "",
          },
        },
        status: {
          type: "enum",
          label: "Status",
          sort: {
            allow: true,
            default: null,
          },
          filter: {
            allow: true,
            matchType: "wildcard",
            value: "",
          },
        },

        collaborationSpaces: {
          type: "avatarStackWithActivityLevel",
          label: "Spaces",
          format(row) {
            return row.collaborationSpaces.map((collaborationSpace) => {
              return {
                src: logoUrl(collaborationSpace),
                href: `/admin/collaboration-spaces/edit/${collaborationSpace.hashedId}`,
                title: collaborationSpace.name,
                activityLevel: collaborationSpace.calc_ActivityLevel,
              };
            });
          },
          sort: {
            allow: false,
          },
          filter: {
            allow: false,
          },
        },
        isSsoFeatureEnabled: {
          type: "boolean",
          label: "SSO",
          sort: {
            allow: true,
            default: null,
          },
          filter: {
            allow: true,
          },
        },
        isNonAdminSpaceCreationEnabled: {
          type: "boolean",
          label: "Non-Admin Spaces",
          hidden: "yes",
          sort: {
            allow: true,
            default: null,
          },
          filter: {
            allow: true,
          },
        },
      },
      createForm: {
        name: {
          label: "Name:",
          placeholder: "Enter name",
          type: "string",
          default: "",
          value: "",
          validatorTypes: ["required"],
        },
        isTestAccount: {
          label: "Test Account:",
          checkboxLabel:
            "This is a test account (exclude from search results, etc.)",
          type: "boolean",
          value: "",
          default: "",
          validatorTypes: [],
        },
        isResearchCompassEnabledByDefault: {
          label: "Research Compass Enabled By Default:",
          checkboxLabel: "Enable Research Compass by default for new spaces",
          type: "boolean",
          value: "",
          default: false,
          validatorTypes: [],
        },
        isSsoFeatureEnabled: {
          label: "SSO Feature Enabled:",
          checkboxLabel:
            "Enable self-serve Single Sign-On (SAML) for this organization",
          type: "boolean",
          value: "",
          default: false,
          validatorTypes: [],
        },
        isNonAdminSpaceCreationEnabled: {
          label: "Non-Admin Space Creation:",
          checkboxLabel:
            "Allow end users (non-admins) to create new spaces in this organization",
          type: "boolean",
          value: "",
          default: true,
          validatorTypes: [],
        },
        status: {
          label: "Status",
          placeholder: "Select status",
          type: "autocomplete-select",
          default: [],
          value: [],
          validatorTypes: [],
          idKey: "value",
          labelKey: "label",
          autocompleteEndpoint: "enum/OrganizationStatus",
          searchOnFocus: true,
        },
      },
      updateForm: {
        hashedId: {
          type: "hidden",
          value: "",
          default: "",
        },
        name: {
          label: "Name:",
          placeholder: "Enter name",
          type: "string",
          value: "",
          default: "",
          validatorTypes: ["required"],
        },
        isTestAccount: {
          label: "Test Account:",
          checkboxLabel:
            "This is a test account (exclude from search results, etc.)",
          type: "boolean",
          value: "",
          default: "",
          validatorTypes: ["required"],
        },
        isResearchCompassEnabledByDefault: {
          label: "Research Compass Enabled By Default:",
          checkboxLabel: "Enable Research Compass by default for new spaces",
          type: "boolean",
          value: "",
          default: false,
          validatorTypes: [],
        },
        isSsoFeatureEnabled: {
          label: "SSO Feature Enabled:",
          checkboxLabel:
            "Enable self-serve Single Sign-On (SAML) for this organization",
          type: "boolean",
          value: "",
          default: false,
          validatorTypes: [],
        },
        isNonAdminSpaceCreationEnabled: {
          label: "Non-Admin Space Creation:",
          checkboxLabel:
            "Allow end users (non-admins) to create new spaces in this organization",
          type: "boolean",
          value: "",
          default: true,
          validatorTypes: [],
        },
        status: {
          label: "Status",
          placeholder: "Select status",
          type: "autocomplete-select",
          default: [],
          value: [],
          validatorTypes: [],
          idKey: "value",
          labelKey: "label",
          autocompleteEndpoint: "enum/OrganizationStatus",
          searchOnFocus: true,
        },
        people: {
          label: "People",
          type: "dataTable",
          value: "",
          singularLabel: "person",
          pluralLabel: "people",
          adminPrefix: "people",
          apiPrefix: "person",
          parentKey: "organization_roles.organization.hashed_id",
          columns: {
            avatar: {
              type: "avatarWithActivityLevel",
              label: "Avatar",
              format(row) {
                return avatarUrl(null, row);
              },
              sort: {
                allow: false,
              },
              filter: {
                allow: false,
              },
            },
            firstName: {
              type: "string",
              label: "First Name",
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
            lastName: {
              type: "string",
              label: "Last Name",
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
            emailAddress: {
              type: "formattedString",
              label: "Email Address",
              format(row) {
                const els = [];
                els.push(`${row.calc_EmailAddress}`);
                if (row.primaryEmailAddress.verifiedAt) {
                  els.push(`<i class="fas fa-check-square ml-2"></i>`);
                }
                return `
                  <span class="text-nowrap">
                    ${els.join("")}
                  </span>
                `;
              },
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
            role: {
              type: "formattedString",
              label: "Role",
              format(row, vm) {
                const els = [];
                const thisRole = row.organizationRoles.find((role) => {
                  return role.organization.hashedId === vm.parentRow.hashedId;
                });
                els.push(`${thisRole?.role?.name}`);
                return `
                  <span class="text-nowrap">
                    ${els.join("")}
                  </span>
                `;
              },
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
          },
          createForm: {
            hashedId: {
              label: "Organization Hashed ID:",
              default(row) {
                return row.hashedId;
              },
              type: "hidden",
              validatorTypes: ["required"],
            },
            person: {
              label:
                "Person (First, Last or Full Name) / Primary Email Address",
              placeholder: "Select person",
              type: "autocomplete-select",
              default: [],
              value: [],
              validatorTypes: ["required"],
              idKey: "hashedId",
              labelKey: "fullName",
              labelFunction(row) {
                return `${row.firstName} ${row.lastName} (${row.calc_EmailAddress}, per-${row.hashedId})`;
              },
              autocompleteEndpoint: "person",
              searchOnFocus: false,
            },
            role: {
              label: "Role",
              placeholder: "Select role",
              type: "autocomplete-select",
              default: [],
              value: [],
              validatorTypes: ["required"],
              idKey: "hashedId",
              labelKey: "name",
              autocompleteEndpoint: "role",
              filters: { role_type: "organization" },
              searchOnFocus: true,
            },
          },
          updateForm: {},
          actions: {
            create: {
              allow: true,
              endpoint(hashedId) {
                return `organization/${hashedId}/add-person/`;
              },
            },
            linkToRecord: {
              allow: true,
              formatUrl(row) {
                return `profiles/edit/${row.profile.hashedId}`;
              },
            },
            update: {
              allow: false,
            },
            delete: {
              allow: false,
            },
            menuItems: [
              {
                label: "Make Admin",
                qualifier(row, vm) {
                  const thisRole = row.organizationRoles.find((role) => {
                    return role.organization.hashedId === vm.parentRow.hashedId;
                  });
                  return (
                    thisRole && thisRole.role.name !== "Organization Admin"
                  );
                },
                confirmFirst: true,
                confirmationMessage:
                  "Are you sure you want to make this person an admin of this Organization?",
                action(event, row, vm) {
                  const url = `organization/${vm.parentRow.hashedId}/change-role/`;
                  return [
                    "apiAction",
                    "postReload",
                    url,
                    {
                      person: { id: row.hashedId },
                      roleName: "Organization Admin",
                    },
                  ];
                },
              },
              {
                label: "Make Member",
                qualifier(row, vm) {
                  const thisRole = row.organizationRoles.find((role) => {
                    return role.organization.hashedId === vm.parentRow.hashedId;
                  });
                  return (
                    thisRole && thisRole.role.name !== "Organization Member"
                  );
                },
                confirmFirst: true,
                confirmationMessage:
                  "Are you sure you want to change this person's role to Organization Member?",
                action(event, row, vm) {
                  const url = `organization/${vm.parentRow.hashedId}/change-role/`;
                  return [
                    "apiAction",
                    "postReload",
                    url,
                    {
                      person: { id: row.hashedId },
                      roleName: "Organization Member",
                    },
                  ];
                },
              },
              {
                label: "Make NotedSource Rep",
                qualifier(row, vm) {
                  const thisRole = row.organizationRoles.find((role) => {
                    return role.organization.hashedId === vm.parentRow.hashedId;
                  });
                  return (
                    thisRole &&
                    thisRole.role.name !== "NotedSource Representative"
                  );
                },
                confirmFirst: true,
                confirmationMessage:
                  "Are you sure you want to make this person a NotedSource Representative for this Organization?",
                action(event, row, vm) {
                  const url = `organization/${vm.parentRow.hashedId}/change-role/`;
                  return [
                    "apiAction",
                    "postReload",
                    url,
                    {
                      person: { id: row.hashedId },
                      roleName: "NotedSource Representative",
                    },
                  ];
                },
              },
              {
                label: "Remove from Organization",
                qualifier() {
                  return true;
                },
                confirmFirst: true,
                confirmationMessage:
                  "Are you sure you want to remove this person from this Organization?",
                action(event, row, vm) {
                  const url = `organization/${vm.parentRow.hashedId}/remove-person/`;
                  return [
                    "apiAction",
                    "postReload",
                    url,
                    { person: { id: row.hashedId } },
                  ];
                },
              },
              {
                label: "Get Magic Link",
                qualifier() {
                  return true;
                },
                action(event, row) {
                  const url = `person/get-magic-link/`;
                  return [
                    "apiAction",
                    "postCopy",
                    url,
                    { emailAddress: row.calc_EmailAddress },
                  ];
                },
              },
            ],
          },
        },
        collaborationSpaces: {
          label: "Spaces",
          type: "dataTable",
          value: "",
          singularLabel: "space",
          pluralLabel: "spaces",
          adminPrefix: "collaboration-spaces",
          apiPrefix: "collaboration-space",
          parentKey: "organization.hashed_id",
          columns: {
            name: {
              type: "string",
              label: "Name",
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
          },
          createForm: {
            hashedId: {
              label: "Organization Hashed ID:",
              default(row) {
                return row.hashedId;
              },
              type: "hidden",
              validatorTypes: ["required"],
            },
            collaborationSpace: {
              label: "Space Name",
              placeholder: "Select space",
              type: "autocomplete-select",
              default: [],
              value: [],
              validatorTypes: ["required"],
              idKey: "hashedId",
              labelKey: "name",
              labelFunction(row) {
                return `${row.name}, grp-${row.hashedId})`;
              },
              autocompleteEndpoint: "collaboration-space",
              searchOnFocus: false,
            },
          },
          updateForm: {},
          actions: {
            create: {
              allow: true,
              endpoint(hashedId) {
                return `organization/${hashedId}/add-collaboration-space/`;
              },
            },
            linkToRecord: {
              allow: true,
              formatUrl(row) {
                return `collaboration-spaces/edit/${row.hashedId}`;
              },
            },
            update: {
              allow: false,
            },
            delete: {
              allow: false,
            },
            menuItems: [
              {
                label: "Remove from Organization",
                qualifier() {
                  return true;
                },
                confirmFirst: true,
                confirmationMessage:
                  "Are you sure you want to remove this Space from this Organization?",
                action(event, row, vm) {
                  const url = `organization/${vm.parentRow.hashedId}/remove-collaboration-space/`;
                  return [
                    "apiAction",
                    "postReload",
                    url,
                    { collaborationSpace: { id: row.hashedId } },
                  ];
                },
              },
            ],
          },
        },
        creditTransactions: {
          label: "Credit Transactions",
          type: "dataTable",
          value: "",
          singularLabel: "credit transaction",
          pluralLabel: "credit transactions",
          adminPrefix: "credit-transactions",
          apiPrefix: "organization-credit-transaction",
          parentKey: "organization.hashed_id",
          columns: {
            creditType: {
              type: "formattedString",
              label: "Credit Type",
              format(row) {
                const els = [];
                els.push(`${row.creditType.label}`);
                return `
                  <span class="text-nowrap">
                    ${els.join("")}
                  </span>
                `;
              },
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
            transactionType: {
              type: "formattedString",
              label: "Transaction Type",
              format(row) {
                const els = [];
                els.push(`${row.transactionType.label}`);
                return `
                  <span class="text-nowrap">
                    ${els.join("")}
                  </span>
                `;
              },
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
            quantity: {
              type: "formattedString",
              label: "Quantity",
              format(row) {
                const els = [];
                if (
                  ["CREDIT", "TRANSFER_IN"].indexOf(row.transactionType.value) >
                  -1
                ) {
                  els.push("+");
                } else {
                  els.push("-");
                }
                els.push(`${row.quantity / 100}`);
                return `
                  <span class="text-nowrap">
                    ${els.join("")}
                  </span>
                `;
              },
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
            costCents: {
              type: "formattedString",
              label: "Client Cost (For CREDIT)",
              format(row) {
                const els = [];
                els.push(
                  `<span>${
                    row?.costCents ? `$${row?.costCents / 100}` : `--` // eslint-disable-line
                  }</span>`,
                );
                return `
                  <span class="text-nowrap">
                    ${els.join("")}
                  </span>
                `;
              },
              sort: {
                allow: false,
                default: null,
              },
              filter: {
                allow: false,
              },
            },
            clientReflectedAt: {
              type: "absoluteDate",
              label: "Client Reflected At",
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
            clientDescription: {
              type: "string",
              label: "Client Description",
              sort: {
                allow: false,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
          },
          createForm: {
            hashedId: {
              label: "Organization Hashed ID:",
              default(row) {
                return row.hashedId;
              },
              type: "hidden",
              validatorTypes: ["required"],
            },
            creditType: {
              label: "Credit Type",
              placeholder: "Select credit type",
              type: "autocomplete-select",
              default: [],
              value: [],
              validatorTypes: ["required"],
              idKey: "value",
              labelKey: "label",
              autocompleteEndpoint: "enum/CreditType",
              searchOnFocus: true,
            },
            transactionType: {
              label: "Transaction Type",
              placeholder: "Select transaction type",
              type: "autocomplete-select",
              default: [],
              value: [],
              validatorTypes: ["required"],
              idKey: "value",
              labelKey: "label",
              autocompleteEndpoint: "enum/OrganizationCreditTransactionType",
              searchOnFocus: true,
            },
            quantity: {
              label: "Quantity of Credits",
              type: "decimalTimesOneHundred",
              placeholder: "Number of credits",
              validatorTypes: ["required"],
            },
            costCents: {
              label: "Client Cost (if transaction type is CREDIT)",
              type: "decimalTimesOneHundred",
              placeholder: "Cost to Client",
              value: "",
              default: "",
              validatorTypes: [],
            },
            internalNotes: {
              label: "Internal Notes:",
              placeholder: "Enter internal notes",
              type: "text",
              value: "",
              default: "",
            },
            clientDescription: {
              label: "Client Description:",
              placeholder: "Enter client description",
              type: "text",
              value: "",
              default: "",
            },
            clientReflectedAt: {
              label: "Client Reflected At:",
              placeholder: "Enter date when this should reflect for the client",
              type: "date",
              value: "",
              default: "",
              validatorTypes: ["required"],
            },
          },
          updateForm: {
            hashedId: {
              type: "hidden",
              value: "",
              default: "",
              validatorTypes: ["required"],
            },
            creditType: {
              label: "Credit Type",
              placeholder: "Select credit type",
              type: "autocomplete-select",
              default: [],
              value: [],
              validatorTypes: ["required"],
              idKey: "value",
              labelKey: "label",
              autocompleteEndpoint: "enum/CreditType",
              searchOnFocus: true,
            },
            transactionType: {
              label: "Transaction Type",
              placeholder: "Select transaction type",
              type: "autocomplete-select",
              default: [],
              value: [],
              validatorTypes: ["required"],
              idKey: "value",
              labelKey: "label",
              autocompleteEndpoint: "enum/OrganizationCreditTransactionType",
              searchOnFocus: true,
            },
            quantity: {
              label: "Quantity of Credits",
              type: "decimalTimesOneHundred",
              placeholder: "Number of credits",
              validatorTypes: ["required"],
            },
            costCents: {
              label: "Client Cost (if transaction type is CREDIT)",
              type: "decimalTimesOneHundred",
              placeholder: "Cost to Client",
              value: "",
              default: "",
              validatorTypes: [],
            },
            internalNotes: {
              label: "Internal Notes:",
              placeholder: "Enter internal notes",
              type: "text",
              value: "",
              default: "",
            },
            clientDescription: {
              label: "Client Description:",
              placeholder: "Enter client description",
              type: "text",
              value: "",
              default: "",
            },
            clientReflectedAt: {
              label: "Client Reflected At:",
              placeholder: "Enter date when this should reflect for the client",
              type: "date",
              value: "",
              default: "",
              validatorTypes: ["required"],
            },
          },
          actions: {
            headerActions: [
              {
                label: "Recall Unused Space Credits",
                icon: "fas fa-undo-alt",
                confirmFirst: true,
                modalTitle: "Recall Unused Space Credits",
                confirmationMessage:
                  "Are you sure you want to recall all unused credits from every Collaboration Space in this organization back to the organization? This will also release any unused project credit allocations and reset all space balances to 0.",
                confirmButtonText: "Recall Credits",
                action(parentRow, vm) {
                  if (!parentRow || !parentRow.hashedId) return;
                  vm.$api.axios
                    .post(
                      `/organization/${parentRow.hashedId}/recall-unused-credits/`,
                    )
                    .then((res) => {
                      const recalled = (res.data?.recalled_credits || 0) / 100;
                      const spacesCount = res.data?.spaces_recalled_count || 0;
                      if (recalled > 0) {
                        vm.$toast.success(
                          `Recalled ${recalled} unused credit(s) from ${spacesCount} space(s) back to the organization.`,
                        );
                      } else {
                        vm.$toast.info(
                          "No unused space credits were found to recall.",
                        );
                      }
                      vm.refresh();
                    })
                    .catch((err) => {
                      vm.$toast.error(
                        err?.response?.data?.message ||
                          "Failed to recall unused credits.",
                      );
                    });
                },
              },
            ],
            create: {
              allow: true,
            },
            update: {
              allow: true,
            },
            delete: {
              allow: true,
            },
            menuItems: [],
          },
        },
        emailDomains: {
          label: "Email Domains",
          type: "dataTable",
          value: "",
          singularLabel: "email domain",
          pluralLabel: "email domains",
          adminPrefix: "email-domains",
          apiPrefix: "organization/email-domain",
          parentKey: "organization.hashed_id",
          columns: {
            domain: {
              type: "string",
              label: "Domain",
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
          },
          createForm: {
            hashedId: {
              label: "Organization Hashed ID:",
              default(row) {
                return row.hashedId;
              },
              type: "hidden",
              validatorTypes: ["required"],
            },
            domain: {
              label: "Email Domain",
              placeholder: "example.com",
              type: "string",
              default: [],
              value: [],
              validatorTypes: ["required"],
            },
          },
          updateForm: {
            hashedId: {
              type: "hidden",
              value: "",
              default: "",
            },
            domain: {
              label: "Email Domain",
              placeholder: "example.com",
              type: "string",
              default: [],
              value: [],
              validatorTypes: ["required"],
            },
          },
          actions: {
            create: {
              allow: true,
            },
            update: {
              allow: true,
            },
            delete: {
              allow: true,
            },
            menuItems: [],
          },
        },
        industries: {
          label: "Industries",
          type: "dataTable",
          value: "",
          singularLabel: "industry",
          pluralLabel: "industries",
          adminPrefix: "industries",
          apiPrefix: "organization/industry",
          parentKey: "organization.hashed_id",
          createForm: {},
          updateForm: {},
          columns: {
            hashedId: {
              type: "formattedString",
              label: "Organization ID",
              format(row) {
                const els = [];
                els.push(
                  `<span class="text-xs uppercase font-mono text-nowrap">cr-${row.hashedId}</span>`,
                );
                return `
                  <span class="text-nowrap">
                    ${els.join("")}
                  </span>
                `;
              },
              sort: {
                allow: false,
              },
              filter: {
                allow: true,
                matchType: "hashedId",
                prefix: "grp-",
              },
            },
            createdAt: {
              type: "relativeDate",
              label: "Created At",
              sort: {
                allow: true,
                default: "desc",
              },
              filter: {
                allow: true,
                matchType: "dateRange",
                value: "",
                // config: https://chmln.github.io/flatpickr/options/
                config: {
                  mode: "range",
                  maxDate: "today",
                },
              },
            },
            industry: {
              type: "enum",
              label: "Industry",
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
          },
          actions: {
            update: {
              allow: false,
            },
            delete: {
              allow: false,
            },
            menuItems: [],
          },
        },
        companySizes: {
          label: "Company Sizes",
          type: "dataTable",
          value: "",
          singularLabel: "company size",
          pluralLabel: "company sizes",
          adminPrefix: "company-sizes",
          apiPrefix: "organization/company-size",
          parentKey: "organization.hashed_id",
          createForm: {},
          updateForm: {},
          columns: {
            hashedId: {
              type: "formattedString",
              label: "Organization ID",
              format(row) {
                const els = [];
                els.push(
                  `<span class="text-xs uppercase font-mono text-nowrap">cr-${row.hashedId}</span>`,
                );
                return `
                  <span class="text-nowrap">
                    ${els.join("")}
                  </span>
                `;
              },
              sort: {
                allow: false,
              },
              filter: {
                allow: true,
                matchType: "hashedId",
                prefix: "grp-",
              },
            },
            createdAt: {
              type: "relativeDate",
              label: "Created At",
              sort: {
                allow: true,
                default: "desc",
              },
              filter: {
                allow: true,
                matchType: "dateRange",
                value: "",
                // config: https://chmln.github.io/flatpickr/options/
                config: {
                  mode: "range",
                  maxDate: "today",
                },
              },
            },
            companySize: {
              type: "enum",
              label: "Company Size",
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
          },
          actions: {
            update: {
              allow: false,
            },
            delete: {
              allow: false,
            },
            menuItems: [],
          },
        },
        companyStages: {
          label: "Company Stages",
          type: "dataTable",
          value: "",
          singularLabel: "company stage",
          pluralLabel: "company stages",
          adminPrefix: "company-stages",
          apiPrefix: "organization/company-stage",
          parentKey: "organization.hashed_id",
          createForm: {},
          updateForm: {},
          columns: {
            hashedId: {
              type: "formattedString",
              label: "Organization ID",
              format(row) {
                const els = [];
                els.push(
                  `<span class="text-xs uppercase font-mono text-nowrap">cr-${row.hashedId}</span>`,
                );
                return `
                  <span class="text-nowrap">
                    ${els.join("")}
                  </span>
                `;
              },
              sort: {
                allow: false,
              },
              filter: {
                allow: true,
                matchType: "hashedId",
                prefix: "grp-",
              },
            },
            createdAt: {
              type: "relativeDate",
              label: "Created At",
              sort: {
                allow: true,
                default: "desc",
              },
              filter: {
                allow: true,
                matchType: "dateRange",
                value: "",
                // config: https://chmln.github.io/flatpickr/options/
                config: {
                  mode: "range",
                  maxDate: "today",
                },
              },
            },
            companyStage: {
              type: "enum",
              label: "companyStage",
              sort: {
                allow: true,
                default: null,
              },
              filter: {
                allow: true,
                matchType: "wildcard",
                value: "",
              },
            },
          },
          actions: {
            update: {
              allow: false,
            },
            delete: {
              allow: false,
            },
            menuItems: [],
          },
        },
      },
      actions: {
        update: {
          allow: true,
        },
        delete: {
          allow: true,
        },
        menuItems: [
          {
            label: "View in Client Cards",
            qualifier() {
              return true;
            },
            action(event, row) {
              const self = this;
              return [
                "executeFunc",
                function _() {
                  self.vm.$router.push(
                    `/admin/client-cards?org=${row.hashedId}`,
                  );
                },
              ];
            },
            vm: this,
          },
          {
            label: "Clone Organization for Demos",
            qualifier() {
              return true;
            },
            action(event, row) {
              const self = this;
              return [
                "executeFunc",
                function _() {
                  self.vm.openCloneModal(row);
                },
              ];
            },
            vm: this,
          },
          {
            label: "Delete Organization",
            qualifier(row) {
              return row.isTestAccount;
            },
            confirmFirst: true,
            confirmationMessage:
              "Are you sure you want to delete this organization? Since it is a test account, all connected spaces and test accounts will also be removed.",
            action(event, row) {
              const url = `organization/${row.hashedId}/`;
              return [
                "apiAction",
                "deleteReload",
                url,
                { organizationId: row.hashedId },
              ];
            },
          },
        ],
      },
    };
  },
  methods: {
    openCloneModal(row) {
      if (!row) return;
      this.cloneSourceOrg = row;
      this.cloneForm.name = `${row.name} (Demo)`;
      this.cloneForm.targetDomain = "demo.notedsource.com";
      this.cloneForm.obfuscateProjects = true;
      this.cloneForm.defaultPassword = "DemoAccess2026!";
      this.cloneForm.logoHash = null;
      this.previewLogoUrl = logoUrl(row);
      this.$refs.cloneOrgModal.toggleModal();
    },
    setCloneLogo(data) {
      if (data && data.files && data.files.length > 0) {
        const file = data.files[0];
        const hash = file.hashedId || file.id;
        this.cloneForm.logoHash = hash;
        this.previewLogoUrl = `${import.meta.env.VITE_API_BASE_URL}/file/${hash}/content/`;
      }
    },
    removeCloneLogo() {
      this.cloneForm.logoHash = null;
      this.previewLogoUrl = this.cloneSourceOrg
        ? logoUrl(this.cloneSourceOrg)
        : "";
    },
    closeCloneModal() {
      if (this.$refs.cloneOrgModal && this.$refs.cloneOrgModal.showModal) {
        this.$refs.cloneOrgModal.toggleModal();
      }
    },
    submitCloneOrg() {
      if (
        !this.cloneSourceOrg ||
        !this.cloneForm.name ||
        !this.cloneForm.targetDomain
      )
        return;
      this.isCloning = true;
      const url = `organization/${this.cloneSourceOrg.hashedId}/clone/`;
      const payload = {
        name: this.cloneForm.name,
        target_domain: this.cloneForm.targetDomain,
        obfuscate_projects: this.cloneForm.obfuscateProjects,
        default_password: this.cloneForm.defaultPassword,
        logo_hash: this.cloneForm.logoHash,
      };

      this.$api.axios
        .post(url, payload)
        .then(() => {
          this.$toast.success("Organization cloned successfully!");
          this.closeCloneModal();
          if (this.$refs.dataTable) {
            this.$refs.dataTable.refresh();
          }
        })
        .catch((err) => {
          const errMsg =
            err.response?.data?.message || "Failed to clone organization.";
          this.$toast.error(errMsg);
        })
        .finally(() => {
          this.isCloning = false;
        });
    },
  },
};
</script>
